import React from 'react';
import { Control, useController, useWatch } from 'react-hook-form';
import { Box, Paper, Typography } from '@mui/material';
import { SelectOption } from 'global';
import { InputField } from 'components/form/rhf/InputField';
import { CurrencyInputField } from 'components/form/rhf/CurrencyInputField';
import { SelectField } from 'components/form/rhf/SelectField';
import { MultiSelectWithCustomOptions } from 'components/form/rhf/MultiSelectWithCustomOptions';
import { APPROVAL_FIELDS } from '../../../constants';
import { ApprovalFieldDef } from '../../../types';

const DECISION_OPTIONS: SelectOption<string>[] = [
  { label: 'Awaiting Review', value: 'Awaiting Review' },
  { label: 'Requested Additional Information', value: 'Requested Additional Information' },
  { label: 'Declined', value: 'Declined' },
  { label: 'Pre-Approved', value: 'Pre-Approved' },
  { label: 'Approved', value: 'Approved' },
];

const TermLengthField: React.FC<{ control: Control<any>; label: string }> = ({ control, label }) => {
  const { field } = useController({ control, name: 'termLengthMonths' });
  const currentValues: string[] = field.value ?? [];
  const options = currentValues.map(v => ({ label: `${v}`, value: v }));

  return (
    <MultiSelectWithCustomOptions
      name="termLengthMonths"
      control={control}
      options={options}
      isOptionsSorted
      onChange={selected => field.onChange(selected.map(String))}
      selectProps={{ label, size: 'small', variant: 'outlined' }}
      selectFormControlProps={{ fullWidth: true, size: 'small' }}
      inputProps={{ label, size: 'small' }}
    />
  );
};

const FieldByType: React.FC<{ field: ApprovalFieldDef; control: Control<any> }> = ({ field, control }) => {
  if (field.key === 'decision') {
    return (
      <SelectField
        name="decision"
        label={field.label}
        control={control}
        options={DECISION_OPTIONS}
        size="small"
        variant="outlined"
        fullWidth
      />
    );
  }

  if (field.key === 'termLengthMonths') {
    return <TermLengthField control={control} label={field.label} />;
  }

  if (field.type === 'currency') {
    return (
      <CurrencyInputField
        name={field.key as any}
        label={field.label}
        control={control}
        textFieldProps={{ size: 'small', variant: 'outlined', fullWidth: true }}
      />
    );
  }

  return (
    <InputField
      name={field.key as any}
      label={field.label}
      control={control}
      inputProps={{
        size: 'small',
        variant: 'outlined',
        fullWidth: true,
        type: field.type === 'number' || field.type === 'percentage' ? 'number' : 'text',
      }}
    />
  );
};

interface ApprovalDetailsFieldsProps {
  control: Control<any>;
  parsedDecisionId: string | null;
}

export const ApprovalDetailsFields: React.FC<ApprovalDetailsFieldsProps> = ({ control, parsedDecisionId }) => {
  const fieldsForSnapshotOnly = ['lenderName', 'applicantName', 'monthlyPayment', 'downPaymentToVendor'];

  const fields = APPROVAL_FIELDS.filter(field => {
    if (field.key === 'docFees') return false;
    if (!parsedDecisionId && fieldsForSnapshotOnly.includes(field.key)) return false;
    return true;
  });

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Approval Details
        </Typography>
      </Box>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fields.map(field => (
          <FieldByType key={field.key} field={field} control={control} />
        ))}
      </Box>
    </Paper>
  );
};
